import { computed, reactive, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { canParticipate, getActivityRainDetail, grabRedPacket } from '@/api/redPacketRain'
import { useAssetsLoader } from '@/hooks/useAssetsLoader'
import { useActivityListData } from '@/composables/useActivityData'
import { useToken } from '@/composables/useToken'
import { getLocaleField } from '@/utils/localeMapping'
import type { LangCode } from '@/types/config'
import type { DiscountDTO } from '@/types/discounts'
import {
  RED_PACKET_RAIN_PHASE,
  RED_PACKET_RAIN_SCHEDULE_STATUS,
  WARNING_DURATION_MS,
  type ActivityConfig,
  type ActivityDetailRaw,
  type RainTimeSettingItem,
  type RedPacketRainPhase,
  type RedPacketRainResult,
  type RedPacketRainScheduleStatus,
  type ScheduleDay,
  type SessionInfo,
  type TimePeriod
} from '@/types/redPacketRain'
const DEBUG = true
const TICK_MODE = {
  Preview: 'preview',
  Warning: 'warning',
  Off: 'off'
} as const

const ZONE = {
  BeforePreview: 'beforePreview',
  Preview: 'preview',
  Joinable: 'joinable',
  AfterJoinable: 'afterJoinable',
  AfterWindow: 'afterWindow'
} as const

type TickMode = (typeof TICK_MODE)[keyof typeof TICK_MODE]
type Zone = (typeof ZONE)[keyof typeof ZONE]

type RedPacketRainInputs = {
  isEmbedded: boolean
  isLoggedIn: boolean
}

type RedPacketRainState = {
  activityDetailRaw: ActivityDetailRaw | null
  activityConfig: ActivityConfig | null
  currentSession: SessionInfo | null
  canJoin: boolean
  phase: RedPacketRainPhase
  result: RedPacketRainResult | null
  timeOffsetMs: number
  displayCountdown: string
  warningSeconds: number
  warningEndAtMs: number
  isWarningDialogVisible: boolean
  isLoadingConfig: boolean
  isSubmitting: boolean
  isCheckingCanJoin: boolean
  inputs: RedPacketRainInputs
}

type Decision = {
  nextPhase: RedPacketRainPhase
  reason: string
  tickMode: TickMode
  zone: Zone
  nextWakeAtMs?: number
  warningEndAtMs?: number
  needCheckCanJoin?: boolean
}

type RedPacketRainController = {
  startOnce: () => void
  initConfig: () => Promise<void>
  checkCanJoin: () => Promise<boolean>
  update: () => void
  requestUpdate: () => void
}

const rainAssets = import.meta.glob('../assets/imgs/activity/rain/*.webp', {
  eager: true,
  query: '?url',
  import: 'default'
})

const getRainAssetUrl = (filename: string) => {
  return (rainAssets[`../assets/imgs/activity/rain/${filename}`] as string | undefined) || ''
}

const parseRainTimeSettings = (jsonStr?: string | null): RainTimeSettingItem[] => {
  try {
    const arr = JSON.parse(jsonStr || '[]')
    return arr.map(
      (item: { sessionId?: string | number; id?: string | number; startTime: number }) => ({
        id: String(item.sessionId ?? item.id),
        startTime: item.startTime
      })
    )
  } catch {
    return []
  }
}

const getTimePeriodStatus = (
  startTime: number,
  validityWindowSec: number,
  preStartTimeHint: number,
  nowMs: number
): RedPacketRainScheduleStatus => {
  const windowEnd = startTime + validityWindowSec * 1000
  const warmupStart = startTime - preStartTimeHint * 60 * 1000

  if (nowMs >= windowEnd) return RED_PACKET_RAIN_SCHEDULE_STATUS.Finished
  if (nowMs >= startTime) return RED_PACKET_RAIN_SCHEDULE_STATUS.InProcess
  if (nowMs >= warmupStart) return RED_PACKET_RAIN_SCHEDULE_STATUS.Preparing
  return RED_PACKET_RAIN_SCHEDULE_STATUS.Upcoming
}

const getDayStatus = (dateKey: string, nowMs: number): RedPacketRainScheduleStatus => {
  const targetDay = dayjs(dateKey).startOf('day')
  const today = dayjs(nowMs).startOf('day')

  if (targetDay.isAfter(today)) return RED_PACKET_RAIN_SCHEDULE_STATUS.Upcoming
  if (targetDay.isSame(today)) return RED_PACKET_RAIN_SCHEDULE_STATUS.InProcess
  return RED_PACKET_RAIN_SCHEDULE_STATUS.Finished
}

type ActivityDetailWithFallback = ActivityDetailRaw & {
  bannerDetail?: string
}

/** 叠加多语言展示字段（与时间/场次逻辑分离） */
const applyLocaleToActivityConfig = (
  base: ActivityConfig,
  raw: ActivityDetailWithFallback,
  locale: LangCode
): ActivityConfig => ({
  ...base,
  activityName: getLocaleField(raw, locale, 'activityName') || raw.activityName || '',
  banner:
    getLocaleField(raw, locale, 'bannerDetail') ||
    getLocaleField(raw, locale, 'image') ||
    raw.bannerDetail ||
    '',
  activityRule: getLocaleField(raw, locale, 'description') || ''
})

const formatActivityConfig = (
  raw: ActivityDetailRaw,
  serverTime: number,
  nowMs: number
): ActivityConfig => {
  const sessions = parseRainTimeSettings(raw.rainTimeSettings)
  const dayMap = new Map<string, RainTimeSettingItem[]>()
  for (const session of sessions) {
    const dateKey = dayjs(session.startTime).format('YYYY-MM-DD')
    const existing = dayMap.get(dateKey) || []
    existing.push(session)
    dayMap.set(dateKey, existing)
  }

  const scheduleDays: ScheduleDay[] = []
  const validWindowSec = Number(raw.delayDuration || 0) * 60

  for (const dateKey of Array.from(dayMap.keys()).sort()) {
    const daySessions = dayMap.get(dateKey)!
    const dateObj = dayjs(dateKey)
    const timePeriods: TimePeriod[] = daySessions
      .sort((a, b) => a.startTime - b.startTime)
      .map((session) => ({
        id: session.id,
        time: dayjs(session.startTime).format('HH:mm'),
        startTime: session.startTime,
        status: getTimePeriodStatus(
          session.startTime,
          validWindowSec,
          Number(raw.preStartTimeHint || 0),
          nowMs
        )
      }))

    scheduleDays.push({
      date: dateObj.valueOf(),
      displayDate: dateObj.format('YYYY-MM-DD'),
      status: getDayStatus(dateKey, nowMs),
      timePeriods
    })
  }

  return {
    activityId: String(raw.activityId),
    activityName: '',
    banner: '',
    activityStartTime: Number(raw.activityStartTime || 0),
    activityEndTime: Number(raw.activityEndTime || 0),
    validityWindow: validWindowSec,
    animationDuration: Number(raw.countdownSeconds || 0),
    previewMinutes: Number(raw.preStartTimeHint || 0),
    activityRule: '',
    serverTime,
    scheduleDays
  }
}

const normalizeActivityDetailRaw = (
  data: ActivityDetailRaw | ActivityDetailRaw[] | null | undefined
): ActivityDetailRaw | null => {
  return Array.isArray(data) ? data[0] || null : data || null
}

const normalizeListActivity = (
  activity: DiscountDTO | null | undefined
): ActivityDetailRaw | null => {
  if (!activity?.activityId) return null
  return {
    ...(activity as unknown as ActivityDetailRaw),
    activityId: activity.activityId,
    activityStartTime: activity.activityStartTime || 0,
    activityEndTime: activity.activityEndTime || 0,
    rainTimeSettings: activity.rainTimeSettings || '[]',
    preStartTimeHint: activity.preStartTimeHint || 0,
    delayDuration: activity.delayDuration || 0,
    countdownSeconds: activity.countdownSeconds || 0,
    activityState: activity.activityState || 0
  }
}

const findNextSession = (
  config: ActivityConfig,
  nowMs: number,
  canJoinMap: Map<string, boolean>
): SessionInfo | null => {
  for (const day of config.scheduleDays) {
    for (const period of day.timePeriods) {
      const windowEnd = period.startTime + config.validityWindow * 1000
      if (nowMs >= windowEnd) continue
      const canJoin = canJoinMap.get(period.id) ?? true
      if (!canJoin) continue
      return {
        id: period.id,
        startTime: period.startTime,
        displayTime: period.time,
        displayDate: dayjs(day.date).format('M月D日')
      }
    }
  }
  return null
}

const findSessionAfter = (config: ActivityConfig, afterStartTime: number): SessionInfo | null => {
  for (const day of config.scheduleDays) {
    for (const period of day.timePeriods) {
      if (period.startTime > afterStartTime) {
        return {
          id: period.id,
          startTime: period.startTime,
          displayTime: period.time,
          displayDate: dayjs(day.date).format('M月D日')
        }
      }
    }
  }
  return null
}

const createDefaultState = (): RedPacketRainState => ({
  activityDetailRaw: null,
  activityConfig: null,
  currentSession: null,
  canJoin: true,
  phase: RED_PACKET_RAIN_PHASE.None,
  result: null,
  timeOffsetMs: 0,
  displayCountdown: '00:00',
  warningSeconds: 5,
  warningEndAtMs: 0,
  isWarningDialogVisible: true,
  isLoadingConfig: false,
  isSubmitting: false,
  isCheckingCanJoin: false,
  inputs: {
    isEmbedded: false,
    isLoggedIn: false
  }
})

const state = reactive(createDefaultState())
let controllerInstance: RedPacketRainController | null = null

function getRedPacketRainController() {
  if (!controllerInstance) controllerInstance = createController()
  return controllerInstance
}

function createController(): RedPacketRainController {
  const { locale } = useI18n()
  const { rawActivities } = useActivityListData()
  const { ensureAssetsLoaded, warmupAssets } = useAssetsLoader({ concurrency: 3 })

  let phaseTimer: ReturnType<typeof setTimeout> | null = null
  let tickTimer: ReturnType<typeof setInterval> | null = null
  let started = false
  let pendingUpdate = false
  let initPromise: Promise<void> | null = null

  const getNow = () => Date.now() + state.timeOffsetMs

  const clearPhaseTimer = () => {
    if (phaseTimer) {
      clearTimeout(phaseTimer)
      phaseTimer = null
    }
  }

  const stopTick = () => {
    if (tickTimer) {
      clearInterval(tickTimer)
      tickTimer = null
    }
  }

  const formatCountdown = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const updateTick = () => {
    const session = state.currentSession
    if (!session) return

    const now = getNow()
    if (state.phase === RED_PACKET_RAIN_PHASE.Preview) {
      const diff = Math.max(0, Math.floor((session.startTime - now) / 1000))
      state.displayCountdown = formatCountdown(diff)
    } else if (state.phase === RED_PACKET_RAIN_PHASE.Warning) {
      state.warningSeconds = Math.max(0, Math.ceil((state.warningEndAtMs - now) / 1000))
    }
  }

  const startTick = () => {
    if (!tickTimer) tickTimer = setInterval(updateTick, 1000)
    updateTick()
  }

  const scheduleWakeAt = (targetMs: number) => {
    const delay = Math.max(0, targetMs - getNow())
    phaseTimer = setTimeout(update, delay)
  }

  const switchToNextSession = (
    activityConfig: ActivityConfig,
    currentStartTime: number,
    previewMinutes: number,
    reason: string
  ): Decision => {
    const nextSession = findSessionAfter(activityConfig, currentStartTime)
    if (nextSession) {
      state.currentSession = nextSession
      state.canJoin = true
      return {
        nextPhase: RED_PACKET_RAIN_PHASE.None,
        reason,
        tickMode: TICK_MODE.Off,
        zone: ZONE.BeforePreview,
        nextWakeAtMs: nextSession.startTime - previewMinutes * 60 * 1000
      }
    }

    return {
      nextPhase: RED_PACKET_RAIN_PHASE.None,
      reason: 'no-more-sessions',
      tickMode: TICK_MODE.Off,
      zone: ZONE.AfterWindow
    }
  }

  const getWarningTextAsset = (currentLocale: string) => {
    if (currentLocale === 'zh-CN') return getRainAssetUrl('warning_text_zh-CN.webp')
    if (currentLocale === 'ms-MY') return getRainAssetUrl('warning_text_en-US.webp')
    return getRainAssetUrl('warning_text_en-US.webp')
  }

  const getAssetsList = () =>
    [
      'warning_bg.webp',
      'warning_coins_splash.webp',
      'atlas.webp',
      'warning_btn.webp',
      'packet.webp',
      'shadow.webp',
      'lantern.webp',
      'mini_packet.webp',
      'dropping_coin.webp',
      'result_float_1.webp',
      'result_float_2.webp',
      'envelope_front.webp',
      'envelope_btn.webp',
      'no_packet.webp'
    ]
      .map(getRainAssetUrl)
      .concat(getWarningTextAsset(locale.value))
      .filter(Boolean)

  const ensurePhaseAssets = async (nextPhase: RedPacketRainPhase) => {
    if (
      nextPhase !== RED_PACKET_RAIN_PHASE.Warning &&
      nextPhase !== RED_PACKET_RAIN_PHASE.Playing
    ) {
      return
    }
    await ensureAssetsLoaded(getAssetsList())
  }

  const checkCanJoin = async (): Promise<boolean> => {
    const config = state.activityConfig
    if (!config) return true

    state.isCheckingCanJoin = true
    try {
      const res = await canParticipate(config.activityId)
      state.canJoin = res.data
      return res.data
    } catch (e) {
      console.error('Failed to check canJoin', e)
      return true
    } finally {
      state.isCheckingCanJoin = false
    }
  }

  const decideNext = (): Decision => {
    const { phase, currentSession, inputs, activityConfig, canJoin } = state
    const now = getNow()

    const stay = (reason: string): Decision => ({
      nextPhase: phase,
      reason,
      tickMode: TICK_MODE.Off,
      zone: ZONE.BeforePreview
    })

    if (!activityConfig || !currentSession) return stay('no-config-or-session')

    const { startTime } = currentSession
    const { validityWindow, animationDuration, previewMinutes } = activityConfig
    const previewStartTime = startTime - previewMinutes * 60 * 1000
    const warningStart = startTime - WARNING_DURATION_MS
    const windowEnd = startTime + validityWindow * 1000
    const rainingCost = WARNING_DURATION_MS + animationDuration * 1000
    const periodEnd = windowEnd - rainingCost

    let zone: Zone
    if (now < previewStartTime) zone = ZONE.BeforePreview
    else if (now >= previewStartTime && now < warningStart) zone = ZONE.Preview
    else if (now >= warningStart && now < periodEnd) zone = ZONE.Joinable
    else if (now >= periodEnd && now < windowEnd) zone = ZONE.AfterJoinable
    else zone = ZONE.AfterWindow

    const makeDecision = (
      nextPhase: RedPacketRainPhase,
      reason: string,
      options: Partial<Decision> = {}
    ): Decision => ({
      nextPhase,
      reason,
      tickMode: TICK_MODE.Off,
      zone,
      ...options
    })

    if (inputs.isEmbedded) return makeDecision(RED_PACKET_RAIN_PHASE.None, 'embedded')
    if (phase === RED_PACKET_RAIN_PHASE.Playing || phase === RED_PACKET_RAIN_PHASE.Result) {
      return makeDecision(phase, 'phase-locked')
    }

    switch (zone) {
      case ZONE.BeforePreview:
        return makeDecision(RED_PACKET_RAIN_PHASE.None, 'before-preview', {
          nextWakeAtMs: previewStartTime
        })

      case ZONE.Preview:
        return makeDecision(RED_PACKET_RAIN_PHASE.Preview, 'preview-window', {
          tickMode: TICK_MODE.Preview,
          nextWakeAtMs: warningStart
        })

      case ZONE.Joinable: {
        if (!inputs.isLoggedIn) {
          return makeDecision(RED_PACKET_RAIN_PHASE.None, 'not-logged-in', {
            nextWakeAtMs: periodEnd
          })
        }

        if (!canJoin) {
          return switchToNextSession(
            activityConfig,
            startTime,
            previewMinutes,
            'participated-switch-next'
          )
        }

        if (phase === RED_PACKET_RAIN_PHASE.Warning) {
          if (state.warningEndAtMs > now) {
            return makeDecision(RED_PACKET_RAIN_PHASE.Warning, 'warning-ongoing', {
              tickMode: TICK_MODE.Warning,
              nextWakeAtMs: state.warningEndAtMs,
              warningEndAtMs: state.warningEndAtMs
            })
          }
          return makeDecision(RED_PACKET_RAIN_PHASE.Playing, 'warning-ended')
        }

        const nextWarningEndAtMs = now + WARNING_DURATION_MS
        return makeDecision(RED_PACKET_RAIN_PHASE.Warning, 'enter-warning', {
          tickMode: TICK_MODE.Warning,
          nextWakeAtMs: nextWarningEndAtMs,
          warningEndAtMs: nextWarningEndAtMs,
          needCheckCanJoin: now >= startTime
        })
      }

      case ZONE.AfterJoinable:
      case ZONE.AfterWindow:
        return switchToNextSession(activityConfig, startTime, previewMinutes, 'switch-next-session')

      default:
        return makeDecision(RED_PACKET_RAIN_PHASE.None, 'unknown-zone')
    }
  }

  const debugLog = (decision: Decision) => {
    if (!DEBUG) return
    const session = state.currentSession
    console.log('[RedPacketRain]', {
      now: new Date(getNow()),
      sessionId: session?.id,
      startTime: session?.startTime ? new Date(session?.startTime) : '',
      zone: decision.zone,
      phase: state.phase,
      nextPhase: decision.nextPhase,
      reason: decision.reason
    })
  }

  const applyDecision = async (decision: Decision) => {
    clearPhaseTimer()

    if (decision.needCheckCanJoin) {
      const canJoin = await checkCanJoin()
      if (!canJoin) {
        update()
        return
      }
    }

    await ensurePhaseAssets(decision.nextPhase)
    const prevPhase = state.phase
    state.phase = decision.nextPhase

    if (
      decision.nextPhase === RED_PACKET_RAIN_PHASE.Warning &&
      prevPhase !== RED_PACKET_RAIN_PHASE.Warning
    ) {
      state.isWarningDialogVisible = true
      state.warningEndAtMs = decision.warningEndAtMs!
    }

    if (decision.tickMode === TICK_MODE.Off) stopTick()
    else startTick()

    if (decision.nextWakeAtMs !== undefined) scheduleWakeAt(decision.nextWakeAtMs)
  }

  const update = () => {
    const decision = decideNext()
    debugLog(decision)
    void applyDecision(decision)
  }

  const requestUpdate = () => {
    if (pendingUpdate) return
    pendingUpdate = true
    queueMicrotask(() => {
      pendingUpdate = false
      update()
    })
  }

  const initConfig = async () => {
    if (initPromise) return initPromise

    initPromise = (async () => {
      state.isLoadingConfig = true
      try {
        let activityDetailRaw: ActivityDetailRaw | null = null
        let timestamp = Date.now()

        if (state.inputs.isLoggedIn) {
          const res = await getActivityRainDetail()
          activityDetailRaw = normalizeActivityDetailRaw(res.data)
          timestamp = res.timestamp || Date.now()
        } else {
          const activity = rawActivities.value.find((it) => Number(it.activityType) === 20)
          activityDetailRaw = normalizeListActivity(activity)
        }

        if (!activityDetailRaw) return

        state.activityDetailRaw = activityDetailRaw
        state.timeOffsetMs = timestamp - Date.now()

        const config = formatActivityConfig(activityDetailRaw, timestamp, getNow())
        state.activityConfig = config
        state.currentSession = findNextSession(config, getNow(), new Map())
        update()
      } catch (e) {
        console.error('Failed to init RedPacketRain config', e)
      } finally {
        state.isLoadingConfig = false
      }
    })().finally(() => {
      initPromise = null
    })

    return initPromise
  }

  const startOnce = () => {
    if (started) return
    started = true
    warmupAssets(getAssetsList())

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearPhaseTimer()
        stopTick()
        return
      }
      if (state.activityDetailRaw) {
        state.activityConfig = formatActivityConfig(
          state.activityDetailRaw,
          state.activityConfig?.serverTime ?? Date.now(),
          getNow()
        )
      }
      update()
    })

    watch(locale, () => {
      warmupAssets(getAssetsList())
    })
  }

  return {
    startOnce,
    initConfig,
    checkCanJoin,
    update,
    requestUpdate
  }
}

export function useRedPacketRain() {
  const controller = getRedPacketRainController()
  const route = useRoute()
  const { locale } = useI18n()
  const { isLoggedIn } = useToken()

  state.inputs.isEmbedded = route.path.includes('/embedded')
  state.inputs.isLoggedIn = isLoggedIn.value
  controller.startOnce()

  watch(
    () => route.path,
    (path: string) => {
      state.inputs.isEmbedded = path.includes('/embedded')
      controller.requestUpdate()
    }
  )

  watch(isLoggedIn, (value: boolean) => {
    state.inputs.isLoggedIn = value
    controller.requestUpdate()
  })

  const closeWarningDialog = () => {
    state.isWarningDialogVisible = false
  }

  const finishRain = async (hitCount: number) => {
    const session = state.currentSession
    const config = state.activityConfig
    if (state.isSubmitting || state.phase === RED_PACKET_RAIN_PHASE.Result || !session || !config) {
      return
    }

    state.isSubmitting = true
    state.phase = RED_PACKET_RAIN_PHASE.Result

    try {
      const res = await grabRedPacket(config.activityId, session.id, hitCount)
      state.result = {
        hitCount,
        rewardAmount: res.data.amount,
        hasReward: res.data.success
      }
      state.canJoin = false
    } catch (e) {
      console.error('Failed to submit RedPacketRain result', e)
      state.result = { hitCount, rewardAmount: 0, hasReward: false }
    } finally {
      state.isSubmitting = false
    }
  }

  const hideResultDialog = () => {
    state.phase = RED_PACKET_RAIN_PHASE.None
  }

  const finalizeResultDialogClose = () => {
    state.result = null
    const config = state.activityConfig
    const currentSession = state.currentSession
    if (config && currentSession) {
      state.currentSession = findSessionAfter(config, currentSession.startTime)
      state.canJoin = true
    }
    controller.update()
  }

  const nextSession = computed<SessionInfo | null>(() => {
    const config = state.activityConfig
    const current = state.currentSession
    if (!config || !current) return null
    return findSessionAfter(config, current.startTime)
  })

  const activityConfig = computed<ActivityConfig | null>(() => {
    const base = state.activityConfig
    const raw = state.activityDetailRaw
    if (!base || !raw) return base
    return applyLocaleToActivityConfig(base, raw, locale.value as LangCode)
  })

  return {
    currentPhase: toRef(state, 'phase'),
    isWarningDialogVisible: toRef(state, 'isWarningDialogVisible'),
    currentSession: toRef(state, 'currentSession'),
    nextSession,
    displayCountdown: toRef(state, 'displayCountdown'),
    warningSeconds: toRef(state, 'warningSeconds'),
    result: toRef(state, 'result'),
    activityConfig,
    scheduleDays: computed<ScheduleDay[]>(() => activityConfig.value?.scheduleDays ?? []),
    animationDuration: computed(() => activityConfig.value?.animationDuration),
    shouldShowWarmupFloat: computed(() => state.phase === RED_PACKET_RAIN_PHASE.Preview),
    shouldShowWarningDialog: computed(
      () => state.phase === RED_PACKET_RAIN_PHASE.Warning && state.isWarningDialogVisible
    ),
    shouldShowRainCanvas: computed(() => state.phase === RED_PACKET_RAIN_PHASE.Playing),
    shouldShowResultDialog: computed(
      () => state.phase === RED_PACKET_RAIN_PHASE.Result && !!state.result
    ),
    closeWarningDialog,
    finishRain,
    hideResultDialog,
    finalizeResultDialogClose,
    initConfig: controller.initConfig
  }
}
